import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@progress/kendo-react-buttons';
import { Invitation } from 'services/api/invitations/types';
import { formatDate } from 'utils/date';
import { useNotificationsContext } from 'context';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { DeleteDialog } from 'app/components/dialogs';
import useFetchEquipmentData from 'app/pages/Equipment/EquipmentNotLoaded/hooks/useFetchAndSetEquipmentData';
import { invitationsApi } from 'services/api/invitations/queries';

interface Props {
  isMobile: boolean;
  invitation: Invitation;
}

function PendingReceivedInvitationRow({ isMobile, invitation }: Props) {
  const { addNotification } = useNotificationsContext();
  const [acceptInvitation, { isLoading: isAcceptInvitationLoading }] =
    invitationsApi.useAcceptInvitationMutation();
  const [declineInvitation, { isLoading: isDeclineInvitationLoading }] =
    invitationsApi.useDeclineInvitationMutation();
  const [isDeclineInvitationDialogOpen, setIsDeclineInvitationDialogOpen] =
    useState(false);
  const fetchAndSetEquipment = useFetchEquipmentData();

  const isLoading = isAcceptInvitationLoading || isDeclineInvitationLoading;

  const invitedAt = formatDate(
    invitation.accessDetails.invitedAt,
    'DATE_FORMAT_COMPACT',
  );
  const [startsAtDate, startsAtTime] = invitation.accessDetails.startsAt
    ? formatDate(
        invitation.accessDetails.startsAt,
        'DATE_FORMAT_SPLIT_TIME',
      ).split('|')
    : [];
  const [endsAtDate, endsAtTime] = invitation.accessDetails.endsAt
    ? formatDate(
        invitation.accessDetails.endsAt,
        'DATE_FORMAT_SPLIT_TIME',
      ).split('|')
    : [];
  // TODO replace with a new hook, like usePrefixedTranslation(namespace: string, prefix: string)
  const { t } = useTranslation();
  const translationKey =
    'access_sharing.pending_received_invitations.table.actions';

  const onAcceptAction = async () => {
    try {
      await acceptInvitation([
        {
          shareType: invitation.sharedItemDetails.shareType,
          exposedAccessSharingRecordId: invitation.exposedAccessSharingRecordId,
        },
      ]).unwrap();
      addNotification(
        <Success
          title={t('access_sharing.notifications.invitation_accepted')}
          text={t('access_sharing.notifications.equipment_successfully_added')}
        />,
      );
      await fetchAndSetEquipment();
    } catch (error) {
      addNotification(<ErrorNotification text={'Server error'} />);
      console.error(error);
    }
  };

  const handleDeclineInvitation = async () => {
    try {
      await declineInvitation([
        {
          shareType: invitation.sharedItemDetails.shareType,
          exposedAccessSharingRecordId: invitation.exposedAccessSharingRecordId,
        },
      ]);
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsDeclineInvitationDialogOpen(false);
    }
  };

  return (
    <>
      <DeleteDialog
        open={isDeclineInvitationDialogOpen}
        title={t('decline_invitation_confirmation', {
          machine: invitation.sharedItemDetails.sharedItemLabel,
          role: t(invitation.accessDetails.sharedRole.toLowerCase()),
        })}
        confirmationLabel={t(
          'access_sharing.pending_received_invitations.table.actions.decline',
        )}
        onClose={() => setIsDeclineInvitationDialogOpen(false)}
        onConfirm={handleDeclineInvitation}
      />

      {!isMobile ? (
        <div className="access-sharing-table-row pending-received-invitation">
          <div className="equipment-table-field ellipsis">
            {invitation.sharedItemDetails.sharedItemLabel}
          </div>
          <div className="equipment-table-field">
            {invitation.accessDetails.sharedRole}
          </div>
          <div className="pending-details min-w-0">
            <span className="equipment-table-field ellipsis">
              {invitation.accessDetails.invitedByName}
            </span>
            <span className="grayed-text">{invitedAt}</span>
          </div>
          <div className="pending-details">
            <span>{startsAtDate}</span>
            <span>{startsAtTime}</span>
          </div>
          <div className="pending-details">
            <span>{endsAtDate}</span>
            <span>{endsAtTime}</span>
          </div>
          <div className="pending-invitation-buttons-container">
            <Button
              className="pending-invitation-button"
              disabled={isLoading}
              onClick={() => setIsDeclineInvitationDialogOpen(true)}
            >
              {t(`${translationKey}.decline`)}
            </Button>
            <Button
              className="pending-invitation-button"
              themeColor="primary"
              onClick={onAcceptAction}
              disabled={isLoading}
            >
              {t(`${translationKey}.accept`)}
            </Button>
          </div>
        </div>
      ) : (
        <div className="pending-invitation-card">
          <div className="title-and-date">
            <div className="pending-invitation-title">
              {invitation.accessDetails.sharedRole}
            </div>
            <div className="pending-invitation-date">{invitedAt}</div>
          </div>
          <div className="access-to-container">
            <div className="bolded-info">{t('access_to')}</div>
            <div>{invitation.sharedItemDetails.sharedItemLabel}</div>
          </div>
          <div className="start-time-container">
            <span className="bolded-info">{t('start_time')}: </span>
            <span>{startsAtDate}</span>
            <span>{startsAtTime}</span>
          </div>
          <div className="invited-by-container">
            <span className="bolded-info">{t('invited_by')}: </span>
            <span>{invitation.accessDetails.invitedByName}</span>
          </div>
          <div className="pending-invitation-buttons-container">
            <Button
              className="pending-invitation-button"
              disabled={isLoading}
              onClick={() => setIsDeclineInvitationDialogOpen(true)}
            >
              {t(`${translationKey}.decline`)}
            </Button>
            <Button
              className="pending-invitation-button"
              themeColor="primary"
              disabled={isLoading}
              onClick={onAcceptAction}
            >
              {t(`${translationKey}.accept`)}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PendingReceivedInvitationRow;
